import { useState, useMemo } from 'react'
import { Signal } from '../../api/signals'
import { inferSeverity } from '../../lib/domain'
import { SignalCard } from './SignalCard'
import { Badge } from '../ui/Badge'
import { format } from 'date-fns'

// ── Constants ──────────────────────────────────────────────────────────

const DOTS_PER_ROW = 12
const DOT_SIZE = 14
const MAX_VISIBLE_SIGNALS = 36 // 3 snake rows
const AUTO_COLLAPSE_THRESHOLD = 20
const COLLAPSE_STORAGE_KEY = 'anveshak_signal_collapse'

const severityColor: Record<string, string> = {
  HIGH: 'var(--signal-high, #ef4444)',
  MEDIUM: 'var(--signal-med, #f59e0b)',
  LOW: 'var(--signal-low, #10b981)',
}

// ── Helpers ─────────────────────────────────────────────────────────────

function getCollapsePrefs(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(COLLAPSE_STORAGE_KEY) || '{}')
  } catch {
    return {}
  }
}

function setCollapsePref(topicId: string, collapsed: boolean): void {
  const prefs = getCollapsePrefs()
  prefs[topicId] = collapsed
  localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(prefs))
}

interface TopicGroup {
  topic_id: string
  topic_name: string
  signals: Signal[]
}

function groupByTopic(signals: Signal[]): TopicGroup[] {
  const map = new Map<string, TopicGroup>()
  for (const s of signals) {
    const key = s.topic_id
    if (!map.has(key)) {
      map.set(key, {
        topic_id: key,
        topic_name: s.topic_name || 'Unknown Topic',
        signals: [],
      })
    }
    map.get(key)!.signals.push(s)
  }
  // Sort topics by signal count descending
  return Array.from(map.values()).sort((a, b) => b.signals.length - a.signals.length)
}

// ── Topic color palette (muted, military-aesthetic) ────────────────────

const TOPIC_COLORS = [
  '#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b',
  '#ec4899', '#6366f1', '#14b8a6', '#f97316', '#a855f7',
]

// ── Snake Row Component ────────────────────────────────────────────────

function SnakeRow({
  signals,
  rowIndex,
  totalRows,
  timeRange,
  topicColor,
  selectedIds,
  onSelectGroup,
}: {
  signals: Signal[]
  rowIndex: number
  totalRows: number
  timeRange: { min: number; max: number }
  topicColor: string
  selectedIds: Set<string>
  onSelectGroup: (ids: string[]) => void
}) {
  const isReversed = rowIndex % 2 === 1
  const range = timeRange.max - timeRange.min || 1

  // Position dots along the row based on timestamp, grouping co-temporal signals
  const positioned = signals.map((s) => {
    const ts = new Date(s.created_at).getTime()
    const pct = ((ts - timeRange.min) / range) * 100
    return { signal: s, pct: Math.max(2, Math.min(98, pct)) }
  })

  // Group dots within 1% of each other (co-temporal)
  const grouped: { signals: Signal[]; pct: number }[] = []
  const sorted = [...positioned].sort((a, b) => a.pct - b.pct)
  for (const dot of sorted) {
    const last = grouped[grouped.length - 1]
    if (last && Math.abs(last.pct - dot.pct) < 1.5) {
      last.signals.push(dot.signal)
    } else {
      grouped.push({ signals: [dot.signal], pct: dot.pct })
    }
  }

  // Reverse positioning for odd rows (snake pattern)
  const dots = isReversed
    ? grouped.map((d) => ({ ...d, pct: 100 - d.pct }))
    : grouped

  // Compute time labels for this row's range segment
  const rowFraction = 1 / (totalRows || 1)
  const rowStartTime = timeRange.min + rowIndex * rowFraction * range
  const rowEndTime = timeRange.min + (rowIndex + 1) * rowFraction * range
  const leftTime = isReversed ? rowEndTime : rowStartTime
  const rightTime = isReversed ? rowStartTime : rowEndTime

  return (
    <div className="relative">
      {/* Time labels on the line */}
      <div className="flex items-center justify-between px-0.5 mb-0.5">
        <span className="text-[9px] text-text-muted font-mono">
          {format(new Date(leftTime), 'MMM d HH:mm')}
        </span>
        <span className="text-[9px] text-text-muted font-mono">
          {format(new Date(rightTime), 'MMM d HH:mm')}
        </span>
      </div>

      {/* Timeline line */}
      <div className="relative h-10 flex items-center">
        <div
          className="absolute inset-x-0 top-1/2 h-[2px] -translate-y-1/2 opacity-30"
          style={{ backgroundColor: topicColor }}
        />

        {/* Snake connector from previous row */}
        {rowIndex > 0 && (
          <div
            className="absolute h-8 w-[2px] opacity-20"
            style={{
              backgroundColor: topicColor,
              [isReversed ? 'right' : 'left']: 0,
              top: '-1.5rem',
            }}
          />
        )}

        {/* Signal dots (grouped when co-temporal) */}
        {dots.map((group) => {
          const primary = group.signals[0]
          const count = group.signals.length
          const severity = inferSeverity(primary)
          const groupIds = group.signals.map((s) => s.id)
          const isSelected = group.signals.some((s) => selectedIds.has(s.id))
          const dotSize = count > 1 ? DOT_SIZE + Math.min(count * 3, 16) : DOT_SIZE
          return (
            <button
              key={primary.id}
              className="absolute -translate-x-1/2 -translate-y-1/2 top-1/2 z-10 group/dot"
              style={{ left: `${group.pct}%` }}
              onClick={(e) => {
                e.stopPropagation()
                onSelectGroup(isSelected ? [] : groupIds)
              }}
              aria-label={primary.cluster_label || primary.description}
            >
              {/* Pulse ring for new signals */}
              {primary.status === 'new' && (
                <span
                  className="absolute inset-0 rounded-full animate-ping opacity-30"
                  style={{
                    width: dotSize + 8,
                    height: dotSize + 8,
                    marginLeft: -(dotSize + 8) / 2 + dotSize / 2,
                    marginTop: -(dotSize + 8) / 2 + dotSize / 2,
                    backgroundColor: severityColor[severity],
                  }}
                />
              )}

              {/* Dot */}
              <span
                className="flex items-center justify-center rounded-full border-2 border-anveshak-bg transition-all duration-200"
                style={{
                  width: isSelected ? dotSize + 4 : dotSize,
                  height: isSelected ? dotSize + 4 : dotSize,
                  backgroundColor: severityColor[severity],
                  boxShadow: isSelected
                    ? `0 0 12px ${severityColor[severity]}80`
                    : `0 0 4px ${severityColor[severity]}40`,
                }}
              >
                {count > 1 && (
                  <span className="text-[8px] font-bold text-white">{count}</span>
                )}
              </span>

              {/* Hover tooltip */}
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 rounded bg-anveshak-card border border-anveshak-border text-[10px] text-text-primary whitespace-nowrap opacity-0 group-hover/dot:opacity-100 transition-opacity pointer-events-none z-20 shadow-lg">
                {count > 1 ? `${count} signals` : (primary.cluster_label || primary.signal_type)}
                <br />
                <span className="text-text-muted">
                  {format(new Date(primary.created_at), 'MMM d, HH:mm')}
                </span>
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Topic Lane Component ───────────────────────────────────────────────

function TopicLane({
  group,
  colorIndex,
  selectedIds,
  onSelectGroup,
  onAcknowledge,
  onDismiss,
  isActioning,
  onShowGraph,
}: {
  group: TopicGroup
  colorIndex: number
  selectedIds: Set<string>
  onSelectGroup: (ids: string[]) => void
  onAcknowledge: (id: string) => void
  onDismiss: (id: string) => void
  isActioning: boolean
  onShowGraph: (id: string) => void
}) {
  const [collapsed, setCollapsed] = useState(() => {
    const prefs = getCollapsePrefs()
    if (group.topic_id in prefs) return prefs[group.topic_id]
    return group.signals.length > AUTO_COLLAPSE_THRESHOLD
  })
  const [showAll, setShowAll] = useState(false)
  const color = TOPIC_COLORS[colorIndex % TOPIC_COLORS.length]

  // Sort signals by time
  const sorted = useMemo(
    () => [...group.signals].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()),
    [group.signals],
  )

  // Cap visible signals — show latest N unless "Show all" toggled
  const visibleSignals = useMemo(
    () => showAll ? sorted : sorted.slice(-MAX_VISIBLE_SIGNALS),
    [sorted, showAll],
  )

  // Time range across visible signals
  const timeRange = useMemo(() => {
    const times = visibleSignals.map((s) => new Date(s.created_at).getTime())
    return { min: Math.min(...times), max: Math.max(...times) }
  }, [visibleSignals])

  // Break into rows (snake segments)
  const rows: Signal[][] = []
  for (let i = 0; i < visibleSignals.length; i += DOTS_PER_ROW) {
    rows.push(visibleSignals.slice(i, i + DOTS_PER_ROW))
  }

  const selectedSignals = visibleSignals.filter((s) => selectedIds.has(s.id))

  const isEven = colorIndex % 2 === 0

  return (
    <div className={`mb-3 rounded-lg px-3 py-2 overflow-hidden ${isEven ? 'bg-white/[0.03]' : 'bg-white/[0.07]'}`}>
      {/* Topic header */}
      <button
        className="flex items-center gap-2 mb-1 px-1 w-full text-left group/lane"
        onClick={() => {
          const next = !collapsed
          setCollapsed(next)
          setCollapsePref(group.topic_id, next)
        }}
      >
        <span
          className="w-3 h-3 rounded-sm shrink-0"
          style={{ backgroundColor: color }}
        />
        <span className="text-sm font-semibold text-text-primary group-hover/lane:text-white transition-colors truncate">
          {group.topic_name}
        </span>
        <Badge variant="ghost" className="text-[10px]">
          {group.signals.length}
        </Badge>
        <span className="text-text-muted text-xs ml-auto">
          {collapsed ? '▸' : '▾'}
        </span>
      </button>

      {/* Timeline rows (snake pattern) */}
      {!collapsed && (
        <div className="pl-5 overflow-hidden">
          {sorted.length > MAX_VISIBLE_SIGNALS && (
            <button
              className="mb-1 text-[10px] text-anveshak-accent hover:underline"
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll
                ? `Show latest ${MAX_VISIBLE_SIGNALS}`
                : `Show all ${sorted.length} signals (${sorted.length - MAX_VISIBLE_SIGNALS} hidden)`}
            </button>
          )}
          <div className="max-h-[400px] overflow-y-auto overflow-x-hidden">
          {rows.map((rowSignals, idx) => (
            <SnakeRow
              key={idx}
              signals={rowSignals}
              rowIndex={idx}
              totalRows={rows.length}
              timeRange={timeRange}
              topicColor={color}
              selectedIds={selectedIds}
              onSelectGroup={onSelectGroup}
            />
          ))}

          </div>
          {/* Expanded signal details (all selected) */}
          {selectedSignals.length > 0 && (
            <div className="mt-2 mb-3 animate-fade-in space-y-2">
              {selectedSignals.length > 1 && (
                <p className="text-[10px] text-text-muted">{selectedSignals.length} signals in this group</p>
              )}
              {selectedSignals.map((sig) => (
                <div key={sig.id}>
                  <div className="flex items-center gap-2 mb-1">
                    <button
                      className="text-[10px] text-anveshak-accent hover:underline"
                      onClick={() => onShowGraph(sig.id)}
                    >
                      View Graph
                    </button>
                  </div>
                  <SignalCard
                    signal={sig}
                    onAcknowledge={onAcknowledge}
                    onDismiss={onDismiss}
                    isActioning={isActioning}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Main Timeline Component ────────────────────────────────────────────

interface SignalTimelineProps {
  signals: Signal[]
  onAcknowledge: (id: string) => void
  onDismiss: (id: string) => void
  isActioning: boolean
  onShowGraph: (id: string) => void
}

export function SignalTimeline({
  signals,
  onAcknowledge,
  onDismiss,
  isActioning,
  onShowGraph,
}: SignalTimelineProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const handleSelectGroup = (ids: string[]) => {
    if (ids.length === 0) setSelectedIds(new Set())
    else setSelectedIds(new Set(ids))
  }

  const topicGroups = useMemo(() => groupByTopic(signals), [signals])

  if (signals.length === 0) return null

  // Time range labels
  const allTimes = signals.map((s) => new Date(s.created_at).getTime())
  const minTime = new Date(Math.min(...allTimes))
  const maxTime = new Date(Math.max(...allTimes))

  return (
    <div>
      {/* Global time range header */}
      <div className="flex items-center justify-between text-[10px] text-text-muted mb-3 px-1">
        <span>{format(minTime, 'MMM d, HH:mm')}</span>
        <div className="flex-1 mx-3 h-[1px] bg-anveshak-border" />
        <span>{format(maxTime, 'MMM d, HH:mm')}</span>
      </div>

      {/* Topic swim lanes */}
      {topicGroups.map((group, idx) => (
        <TopicLane
          key={group.topic_id}
          group={group}
          colorIndex={idx}
          selectedIds={selectedIds}
          onSelectGroup={handleSelectGroup}
          onAcknowledge={onAcknowledge}
          onDismiss={onDismiss}
          isActioning={isActioning}
          onShowGraph={onShowGraph}
        />
      ))}
    </div>
  )
}
